import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

export function useSelfOptimistic<T>(
	passthrough: T,
	refreshDeps?: React.DependencyList,
): readonly [T, React.Dispatch<React.SetStateAction<T>>] {
	const [value, setValue] = useState<T>(passthrough);

	useEffect(() => {
		setValue(passthrough);
	}, refreshDeps ?? [passthrough]);

	return [value, setValue] as const;
}

export function LoadingHoc({
	children,
}: {
	children: (props: {
		isLoading: boolean;
		loadingIcon: (className?: string) => React.ReactNode;
		wrapPromise: <T>(p: Promise<T>) => Promise<T>;
	}) => React.ReactNode;
}) {
	const [_loading, _setLoading] = useSelfOptimistic(false, []);
	return children({
		isLoading: _loading,
		loadingIcon: (className?: string) => (
			<Loader2 className={cn("size-4.5 animate-spin", className)} />
		),
		wrapPromise: async (p: Promise<void>) => {
			if (_loading) {
				return;
			}
			try {
				_setLoading(true);
				return await p;
			} catch (e) {
				console.error(e);
			} finally {
				_setLoading(false);
			}
		},
	});
}
